import { useEffect, useState } from 'react';

import { IStrategy, IStrategyAsset } from '../../../types/model';
import { useAuth } from '../../../context/Auth';
import { useStrategiesContext } from '../StrategiesContext';
import { TTickerCurrency } from '../../../types/model';
import { useUserStrategies } from '../../../context/UserStrategies';

import { FooterNavigator } from '../../Footer/FooterNavigator';
import { Sort } from '../../NewStrategy/Sort';
import { Column } from '../../NewStrategy/StrategyTable';
import { COLUMNS_ALIGN } from '../AlignActive';
import { AlignmentRow } from '../AlignmentRow';
import { AlignTotal } from '../AlignTotal';
import { Wrapper } from '../Wrapper';
import { UpgradeSubscription } from '../../UpgradeSubscription/UpgradeSubscription';

import { useSort } from '../../../hooks/useSort';

import {
  getAlignAssets,
  getSymbol,
  getTotalValues,
} from '../../../helpers/functions';

import { Currency } from '../../../types/common';
import { useTranslation } from 'react-i18next';
import StrategySearch from '../StrategySearch';
import { Icon } from '../../UI';
import moment from 'moment';
import { PlaceholderStrategy } from '../Placeholder';
import { useTutorialContext } from '../../../context/Tutorial';
import { CurrencyCodeEnum } from '../../../constants/enums';

export const Align = ({
  currency = CurrencyCodeEnum.USD,
}: {
  currency?: Currency['code'];
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { strategies } = useUserStrategies();
  const { dispatch } = useStrategiesContext();

  const {
    handleTooltipIconVisibility,
    // AlignFirstAssetTooltip,
    AlignTutorial,
    dispatch: dispatchTutorial,
  } = useTutorialContext();

  useEffect(() => {
    if (user?.membership?.membershipPlan?.sku !== 'Camelot') {
      handleTooltipIconVisibility('ALIGN');
      ALIGN && dispatchTutorial({ isTooltipIconVisible: ALIGN });
    }
  }, [user?.membership?.membershipPlan?.sku]);

  const { type, getSortValue, handleSort } = useSort([...COLUMNS_ALIGN]);

  // const [showResults, setShowResults] = useState<boolean>(false);

  const [fromStrategies, setFromStrategies] = useState<IStrategy[]>([]);
  const [toStrategies, setToStrategies] = useState<IStrategy[]>([]);

  const {
    showTutorial: { ALIGN },
  } = user!.profile!;

  const symbol = getSymbol(currency);

  const fromAssets = fromStrategies.reduce(
    (arr, { strategyAssets: { items } }) => arr.concat(items),
    [] as IStrategyAsset[]
  );

  const toAssets = toStrategies.reduce(
    (arr, { strategyAssets: { items } }) => arr.concat(items),
    [] as IStrategyAsset[]
  );

  // useEffect(() => {
  //   if (!fromStrategies.length || !toStrategies.length) {
  //     setShowResults(false);
  //   }
  // }, [fromStrategies, toStrategies]);

  const tableAssets = getAlignAssets([fromAssets, toAssets]);

  const { totalValue } = getTotalValues(
    tableAssets,
    currency.toLocaleLowerCase() as TTickerCurrency,
    // the date is not correct, but it does not affect the calculation of the totalValue
    moment().format('YYYY-MM-DD')
  );

  // SORT
  type === 'TO DO' && tableAssets.sort((a, b) => getSortValue(a.todo, b.todo));
  type === 'ASSET' && tableAssets.sort((a, b) => getSortValue(a.name, b.name));
  type === 'QUANTITY' &&
    tableAssets.sort((a, b) => getSortValue(a.unit, b.unit));
  type === 'TOTAL VALUE' &&
    tableAssets.sort((a, b) => {
      const valA =
        a.unit * (a.asset.history[0]?.[currency.toLocaleLowerCase()] ?? 0);
      const valB =
        b.unit * (b.asset.history[0]?.[currency.toLocaleLowerCase()] ?? 0);

      return getSortValue(valA, valB);
    });

  const nonArchivedStrategies = strategies.filter(
    (strategy) => !strategy.archived
  );

  return (
    <>
      <AlignTutorial />

      <div className={'h-auto'}>
        {user?.membership?.sku?.includes('Camelot') ? (
          <UpgradeSubscription
            onCloseClick={() => dispatch({ strategiesOption: 'Strategies' })}
            isOpen
            feature="COMMON"
            viewMode="modal"
          />
        ) : strategies.length > 1 ? (
          <div className={'mt-12'}>
            <div className={'flex flex-col w-8/12 mx-auto'}>
              <div className={'mb-6 w-1/2 mx-auto'}>
                <h1
                  className={
                    'text-lg text-center font-semibold dark:text-white'
                  }
                >
                  {t('strategies.align.title')}
                </h1>
                <p className={'text-sm text-center text-grey'}>
                  {t('strategies.align.subtitle')}
                </p>
              </div>
              <div
                className={'flex flex-row justify-between items-center gap-2'}
              >
                <div className={'w-full max-w-80'}>
                  <p
                    className={'uppercase mb-2 text-xs  text-grey font-medium'}
                  >
                    {t('strategies.align.from')}
                  </p>
                  <StrategySearch
                    className={'w-full'}
                    strategies={nonArchivedStrategies}
                    setSelectedStrategies={setFromStrategies}
                    selectedStrategies={fromStrategies}
                  />
                </div>
                <div className={''}>
                  <Icon svgName={'fromTo'} className={'mt-6'} />
                </div>
                <div className={'w-full max-w-80'}>
                  <p className={'uppercase mb-2 text-xs text-grey font-medium'}>
                    {t('strategies.align.to')}
                  </p>
                  <StrategySearch
                    className={'w-full'}
                    strategies={nonArchivedStrategies}
                    setSelectedStrategies={setToStrategies}
                    selectedStrategies={toStrategies}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-32">
            <PlaceholderStrategy
              title={t('placeholders.alignStrategies.title')}
              subtitle={t('placeholders.alignStrategies.subtitle')}
            />
          </div>
        )}
        {fromStrategies.length > 0 && toStrategies.length > 0 && (
          <Wrapper
            title={'Align'}
            className="h-150 mt-8 overflow-hidden"
            displayFooter={false}
          >
            <div className="flex flex-col flex-grow-1 justify-between h-auto overflow-hidden">
              {/* TABLE HEADER */}
              <div className="grid grid-cols-7 py-3 border-t border-b border-grey-10 dark:border-grey-70">
                {COLUMNS_ALIGN.map((columnName, i) => (
                  <Column
                    key={columnName}
                    element={
                      <Sort
                        className={`
                          flex
                          items-center
                          text-sm
                          font-medium
                        `}
                        columnName={columnName}
                        type={type}
                        handleSort={handleSort}
                      />
                    }
                    span={i === 1 ? 1 : 2}
                  />
                ))}
              </div>

              {/* ASSETS */}
              <div className="flex flex-col flex-grow-1 overflow-y-scroll">
                {tableAssets.map((asset) => (
                  <AlignmentRow key={asset.id} asset={asset} />
                ))}
              </div>

              {/* TOTAL */}
              <div className="flex flex-col mt-auto">
                <AlignTotal symbol={symbol} totalValue={totalValue} />
              </div>

              {/* <FooterNavigator
                buttonsPosition="end"
                className="rounded-b-xl z-10 bg-white dark:bg-grey-90 flex-shrink-0"
                saveButtonName="Reset Selection"
                saveButtonVariant="secondary"
                oneButton
                onSave={() => {
                  setShowResults(false);
                  setFromStrategies([]);
                  setToStrategies([]);
                }}
              /> */}
            </div>
          </Wrapper>
        )}
        {/* {!showResults && (
          <FooterNavigator
            buttonsPosition="end"
            className={`border-t
            left-0
            right-0
            bottom-0
            border-grey-10
            bg-white
            fixed
            flex-grow-0
            flex-shrink-0
            dark:bg-grey-90
            dark:border-grey-80
          `}
            saveButtonName="Continue"
            saveButtonVariant="primary"
            oneButton
            isDisabledSaveButton={
              !fromStrategies.length || !toStrategies.length
            }
            onSave={() => {
              setShowResults(true);
            }}
          />
        )} */}
      </div>
    </>
  );
};
